import {
  useState, useEffect, useRef, useCallback,
} from 'react';

const SCROLL_RESTORATION_DELAY = 200;
const SCROLL_UP_THRESHOLD = 100;

const useScrollManagement = ({ isOpen, messages, isAiThinking }) => {
  const messagesEndRef = useRef(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isRestoringScroll, setIsRestoringScroll] = useState(false);
  const [isScrolledUp, setIsScrolledUp] = useState(false);
  const [hasContentBelow, setHasContentBelow] = useState(false);
  const [hasContentAbove, setHasContentAbove] = useState(false);
  const [isAutoScrolling, setIsAutoScrolling] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const prevMessageCountRef = useRef(0);
  const autoScrollTimerRef = useRef(null);
  const isScrolledUpRef = useRef(false);

  const getContainer = useCallback(() => messagesEndRef.current?.parentElement, []);

  const scrollToNewMessage = useCallback((targetIndex) => {
    const container = getContainer();
    if (!container) return;
    const messageEls = container.querySelectorAll('[data-message]');
    const targetEl = messageEls[targetIndex];
    if (targetEl) {
      const containerRect = container.getBoundingClientRect();
      const targetRect = targetEl.getBoundingClientRect();
      container.scrollTo({
        top: container.scrollTop + (targetRect.top - containerRect.top),
        behavior: 'smooth',
      });
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [getContainer]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    setUnreadCount(0);
  }, []);

  // Track whether user has scrolled up from the bottom
  useEffect(() => {
    const container = getContainer();
    if (!container) return undefined;

    const handleScroll = () => {
      const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
      const scrolledUp = distanceFromBottom > SCROLL_UP_THRESHOLD;
      isScrolledUpRef.current = scrolledUp;
      setIsScrolledUp(scrolledUp);
      setHasContentAbove(container.scrollTop > SCROLL_UP_THRESHOLD);
      if (!scrolledUp) setUnreadCount(0);
    };
    handleScroll();

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [messages, getContainer]);

  // Track whether there's content below the viewport via IntersectionObserver
  useEffect(() => {
    const sentinel = messagesEndRef.current;
    const container = getContainer();
    if (!sentinel || !container) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => setHasContentBelow(!entry.isIntersecting),
      { root: container, threshold: 0.1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [messages, getContainer]);

  const prevIsOpenRef = useRef(isOpen);
  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    const isNowClosed = !isOpen;

    if (wasOpen && isNowClosed) {
      const container = getContainer();
      if (container) {
        setScrollPosition(container.scrollTop);
      }
    }

    prevIsOpenRef.current = isOpen;
  }, [isOpen, getContainer]);

  useEffect(() => {
    if (isOpen && scrollPosition > 0) {
      setIsRestoringScroll(true);
      setTimeout(() => {
        const container = getContainer();
        if (container) {
          container.scrollTop = scrollPosition;
        }
        setIsRestoringScroll(false);
      }, SCROLL_RESTORATION_DELAY);
    }
  }, [isOpen]);

  // Scroll thinking indicator into view when it appears
  useEffect(() => {
    if (isAiThinking && !isRestoringScroll && !isScrolledUpRef.current) {
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      });
    }
  }, [isAiThinking, isRestoringScroll]);

  // Scroll to top of new message when messages are added — but only if user
  // was near the bottom or just sent a message themselves. If the user is
  // reading earlier content, leave their scroll alone and increment unread count.
  useEffect(() => {
    if (isRestoringScroll) {
      prevMessageCountRef.current = messages.length;
      return;
    }
    if (prevMessageCountRef.current === 0 && messages.length > 0) {
      // Initial messages — no animated scroll needed
      prevMessageCountRef.current = messages.length;
      return;
    }
    if (messages.length > prevMessageCountRef.current) {
      const newMessages = messages.slice(prevMessageCountRef.current);
      const lastNewMessageIsUser = newMessages[newMessages.length - 1]?.type === 'user';
      const anyNewIsDeferred = newMessages.some((m) => m.deferred);
      const container = getContainer();

      let wasAtBottom = true;
      if (container) {
        const messageEls = container.querySelectorAll('[data-message]');
        const newMessageEls = Array.from(messageEls).slice(prevMessageCountRef.current);
        const newHeight = newMessageEls.reduce((sum, el) => sum + el.getBoundingClientRect().height, 0);
        const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
        wasAtBottom = (distanceFromBottom - newHeight) <= SCROLL_UP_THRESHOLD;
      }

      const shouldAutoScroll = lastNewMessageIsUser || (!anyNewIsDeferred && wasAtBottom);

      if (shouldAutoScroll) {
        const targetIndex = prevMessageCountRef.current;
        setIsAutoScrolling(true);
        clearTimeout(autoScrollTimerRef.current);
        requestAnimationFrame(() => {
          scrollToNewMessage(targetIndex);
          autoScrollTimerRef.current = setTimeout(() => {
            setIsAutoScrolling(false);
          }, 600);
        });
      } else {
        setUnreadCount((c) => c + newMessages.length);
      }
    }
    prevMessageCountRef.current = messages.length;
  }, [messages, isRestoringScroll, scrollToNewMessage, getContainer]);

  const saveScrollPosition = () => {
    const container = getContainer();
    if (container) {
      setScrollPosition(container.scrollTop);
    }
  };

  return {
    messagesEndRef,
    scrollPosition,
    setScrollPosition,
    saveScrollPosition,
    isScrolledUp,
    hasContentBelow: hasContentBelow && !isAutoScrolling,
    hasContentAbove,
    scrollToBottom,
    unreadCount,
  };
};

export default useScrollManagement;
